import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";

const KIND_STYLE = {
  official: "text-white",
  benchmark: "text-ochre",
  estimate: "text-maasai",
};

const pretty = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const num = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value !== "number") return String(value);
  if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(2)}bn`;
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(2)}m`;
  return value.toLocaleString();
};

export default function Sources() {
  const [data, setData] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Sources and Method",
    description:
      "Every figure on this site declared in one place: who publishes it, where to read it, whether it is official, a benchmark or our own estimate, when it was last read and how it is refreshed.",
  });

  useEffect(() => {
    api.get("/sources")
      .then((r) => setData(r.data))
      .catch(() => setErr("We could not load the source registry. Refresh to try again."));
    api.get("/verification").then((r) => setVocab(r.data)).catch(() => setVocab(null));
  }, []);

  const groups = [
    ["Official data", "official"],
    ["Football benchmarks", "benchmark"],
    ["Our own estimates", "estimate"],
  ];

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="sources-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Sources and method</p>
          <h1 className="display-1 mt-5 max-w-4xl">Every Number, And Where It Came From</h1>
          <p className="body-copy mt-6 max-w-3xl">
            Nothing on this site renders a figure that is not declared here. Each entry names the publisher, links to
            the page you can read it on, says whether it is official data, a football benchmark or our own modelling,
            and shows when it was last read and how it is kept current.
          </p>
        </Reveal>

        {err ? (
          <p className="mt-10 border border-maasai/40 bg-maasai/10 p-6 text-sm text-white" data-testid="sources-error">
            {err}
          </p>
        ) : null}

        {data ? (
          <>
            <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="sources-summary">
              {[
                ["Declared sources", data.rows.length, "Every figure on the site"],
                ["Automated feeds", data.rows.filter((r) => r.automated).length, "Read by machine, not by hand"],
                [
                  "Dollar to shilling",
                  `KSh ${Math.round(data.fx.kes_per_usd)}`,
                  `As at ${pretty(data.fx.as_of)}`,
                ],
                [
                  "Waiting for review",
                  data.pending_changes,
                  data.pending_changes ? "Changes held for approval" : "Nothing pending",
                ],
              ].map(([label, value, note]) => (
                <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`sources-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="overline !text-zinc-500">{label}</p>
                  <p className="mono mt-2 text-xl font-bold text-ochre md:text-2xl">{value}</p>
                  <p className="mt-2 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">{note}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="sources-policy">              <p className="overline text-ochre">How updates are handled</p>
              <ul className="mt-4 space-y-3">
                {data.policy.map((line) => (
                  <li key={line} className="text-sm leading-relaxed text-zinc-300">
                    {line}
                  </li>
                ))}
              </ul>
            </div>

            {vocab ? (
              <section className="mt-12 border border-white/10 bg-[#0b0b0b] p-6 md:p-8" data-testid="sources-legend">
                <p className="overline text-ochre">How to read our labels</p>
                <h2 className="display-3 mt-4">Six Labels, One Vocabulary</h2>
                <p className="body-copy mt-4 max-w-3xl">
                  Every figure on this site carries one of these six labels, taken from the source registry
                  below, so a number cannot be dressed up as something its source does not support. The whole
                  registry was last reviewed by hand on {pretty(vocab.review_pass)}.
                </p>
                <div className="mt-7 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3">
                  {vocab.labels.map((l) => (
                    <div key={l.key} className="bg-[#0b0b0b] p-5" data-testid={`legend-${l.key}`}>
                      <VerifyChip v={l.key} meaning={l.meaning} />
                      <p className="mt-3 text-xs leading-relaxed text-zinc-400">{l.meaning}</p>
                      <p className="mono mt-3 text-[0.5rem] uppercase tracking-[0.16em] text-zinc-600">
                        {vocab.counts[l.key]} declared source{vocab.counts[l.key] === 1 ? "" : "s"}
                      </p>
                    </div>
                  ))}
                </div>
                <ul className="mt-7 space-y-3">
                  {vocab.policy.map((line) => (
                    <li key={line} className="max-w-4xl text-sm leading-relaxed text-zinc-300">{line}</li>
                  ))}
                </ul>
                <Link to="/sources" className="btn-ghost mt-7 inline-block" data-testid="sources-corrections-link">
                  The corrections log
                </Link>
              </section>
            ) : null}

            {groups.map(([heading, kind]) => (
              <section key={kind} className="mt-16" data-testid={`sources-group-${kind}`}>
                <h2 className="display-3 border-b border-white/15 pb-4">{heading}</h2>
                <div className="mt-6 overflow-x-auto border border-zinc-800">
                  <table className="w-full min-w-[900px] border-collapse text-left">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                        {["Figure", "Label", "Publisher", "What it feeds", "Latest reading", "Published", "Reviewed", "Refresh"].map((h) => (
                          <th key={h} className="px-4 py-3 text-[0.6rem] font-bold uppercase tracking-[0.18em] text-zinc-300">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.filter((r) => r.kind === kind).map((r) => (
                        <tr key={r.id} className="border-b border-zinc-900 align-top" data-testid={`source-row-${r.id}`}>
                          <td className={`px-4 py-4 text-sm ${KIND_STYLE[r.kind]}`}>{r.label}</td>
                          <td className="px-4 py-4">
                            <VerifyChip v={r.verification} meaning={r.verification_meaning} testId={`source-label-${r.id}`} />
                          </td>
                          <td className="px-4 py-4 text-sm text-zinc-300">
                            {r.url?.startsWith("http") ? (
                              <a
                                href={r.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 hover:text-ochre"
                              >
                                {r.publisher} <ExternalLink size={11} />
                              </a>
                            ) : (
                              r.publisher
                            )}
                          </td>
                          <td className="px-4 py-4 text-xs leading-relaxed text-zinc-400">{r.feeds}</td>
                          <td className="mono px-4 py-4 text-sm text-ochre">{num(r.value)}</td>
                          <td className="mono px-4 py-4 text-xs text-zinc-400">{r.published ? pretty(r.published) : "Not stated"}</td>
                          <td className="mono px-4 py-4 text-xs text-zinc-400">
                            {pretty(r.reviewed)}
                            <span className="mt-1 block text-[0.5rem] uppercase tracking-[0.14em] text-zinc-600">
                              read {pretty(r.as_of || r.fetched_at)} · next {pretty(r.next_review)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-[0.6rem] uppercase tracking-[0.14em]">
                            <span className="text-zinc-400">
                              {r.method} · {r.cadence}
                            </span>
                            <span className="mt-2 flex items-center gap-1">
                              {r.stale || !r.ok ? (
                                <span className="flex items-center gap-1 text-maasai">
                                  <AlertTriangle size={11} /> needs a read
                                </span>
                              ) : r.automated ? (
                                <span className="flex items-center gap-1 text-ochre">
                                  <CheckCircle2 size={11} /> current
                                </span>
                              ) : null}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}

            <div className="mt-16 border border-white/10 bg-[#0b0b0b] p-7" data-testid="sources-cost-inputs">
              <p className="overline">The Kenyan cost inputs behind every step</p>
              <div className="mt-5 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(data.values).map(([key, value]) => (
                  <div key={key} className="bg-[#0b0b0b] p-5">
                    <p className="text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
                      {key.replace(/_/g, " ")}
                    </p>
                    <p className="mono mt-2 text-sm text-ochre">{num(value)}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-400">
                Wages, electricity, water, rent, materials and statutory fees are what every Kenyan cost line in the{" "}
                <Link to="/plans" className="text-ochre underline decoration-maasai underline-offset-4">
                  recommended steps
                </Link>{" "}
                is built from, indexed to each county rather than to a national average. The outcomes — what a produced
                player or a licensed club is worth — are priced on established professional football markets.
              </p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
